/**
 * Hardened RudderStack (CDP) client for booking-v4.
 *
 * RudderStack is normally loaded by the GTM container (the official v3 snippet,
 * which sets `window.rudderanalytics` to a buffering array stub and then calls
 * `.load(writeKey, dataPlaneUrl)`). The risk: that snippet lives *inside* GTM,
 * so until GTM executes, `window.rudderanalytics` is `undefined` and any
 * track/identify call made by the app would throw or be silently lost.
 *
 * This module hardens that:
 *   1. ensureStub() recreates the same buffering array stub the official snippet
 *      creates, whenever `window.rudderanalytics` is absent. App-side calls then
 *      ALWAYS queue and are replayed once the SDK loads (via GTM or our fallback).
 *      This is what guarantees "we can still push even if GTM hasn't loaded".
 *   2. scheduleSelfLoadFallback() — ONLY if VITE_RUDDERSTACK_WRITE_KEY +
 *      VITE_RUDDERSTACK_DATA_PLANE_URL are configured — injects the SDK and calls
 *      `.load()` ourselves if GTM hasn't loaded RudderStack within a short window,
 *      so delivery does not depend on GTM at all. The snippet's `snippetExecuted`
 *      guard prevents a double-load if GTM later runs.
 */
import { IS_SANDBOX } from "../../configs";

interface RudderStackSDK {
  load: (
    writeKey: string,
    dataPlaneUrl: string,
    options?: Record<string, unknown>,
  ) => void;
  ready: (callback: () => void) => void;
  page: (...args: unknown[]) => void;
  track: (event: string, properties?: Record<string, unknown>) => void;
  identify: (userId: string, traits?: Record<string, unknown>) => void;
  reset: () => void;
  snippetExecuted?: boolean;
  [key: string]: unknown;
}

const STUB_METHODS = [
  "setDefaultInstanceKey",
  "load",
  "ready",
  "page",
  "track",
  "identify",
  "alias",
  "group",
  "reset",
  "setAnonymousId",
  "startSession",
  "endSession",
  "consent",
] as const;

// Mirrors the GTM snippet (RudderSnippetVersion 3.0.60, modern build).
const SDK_URL = "https://cdn.rudderlabs.com/v3/modern/rsa.min.js";
const WRITE_KEY = import.meta.env.VITE_RUDDERSTACK_WRITE_KEY as
  | string
  | undefined;
const DATA_PLANE_URL = import.meta.env.VITE_RUDDERSTACK_DATA_PLANE_URL as
  | string
  | undefined;
const LOAD_FALLBACK_DELAY_MS = 4000;

// CDP sending is gated on the sandbox flag: we send from any NON-sandbox build.
// Per product decision this includes production AND training (training carries
// real, production-like data); staging / testing / vitest set
// VITE_IS_SANDBOX=true and are excluded. Mirrors the send-page-view guard.
const CDP_ENABLED = !IS_SANDBOX;

/** True when CDP sending is enabled (any non-sandbox build). */
export const isCdpEnabled = (): boolean => CDP_ENABLED;

// Bridge type: window.rudderanalytics is either the array stub (pre-load) or the
// loaded SDK. We treat it loosely and let ensureStub() normalize access.
const getRA = (): RudderStackSDK | undefined =>
  typeof window !== "undefined"
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ((window as any).rudderanalytics as RudderStackSDK | undefined)
    : undefined;

const isLoaded = (): boolean => {
  const ra = getRA();
  // The loaded SDK replaces the array stub with an object exposing real methods.
  return !!ra && !Array.isArray(ra) && typeof ra.track === "function";
};

/**
 * Ensure `window.rudderanalytics` exists as a buffering array stub if GTM hasn't
 * created it yet. Compatible with the official snippet: queued calls are stored
 * as `[method, ...args]` and replayed when the real SDK loads. No-op if GTM (or a
 * prior call) already created it.
 */
export const ensureStub = (): void => {
  if (!CDP_ENABLED) return;
  if (typeof window === "undefined") return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  if (w.rudderanalytics) return;

  const queue: unknown[] = [];
  STUB_METHODS.forEach((method) => {
    (queue as unknown as Record<string, unknown>)[method] = (
      ...args: unknown[]
    ) => {
      queue.push([method, ...args]);
    };
  });
  w.rudderanalytics = queue;
};

/**
 * If a write key + data plane are configured, load the SDK ourselves when GTM
 * has not done so within LOAD_FALLBACK_DELAY_MS. Inert when the env vars are
 * unset (the default) — in that case we rely entirely on GTM + the buffer stub.
 */
export const scheduleSelfLoadFallback = (): void => {
  if (!CDP_ENABLED) return;
  if (!WRITE_KEY || !DATA_PLANE_URL) return;
  if (typeof window === "undefined") return;

  window.setTimeout(() => {
    if (isLoaded()) return; // GTM already loaded the SDK — nothing to do.

    ensureStub();
    const ra = getRA();
    if (!ra) return;

    // Block GTM's snippet from double-loading if it runs after us.
    ra.snippetExecuted = true;

    const script = document.createElement("script");
    script.async = true;
    script.src = SDK_URL;
    script.setAttribute("data-loader", "RS_JS_SDK");
    script.setAttribute("data-rsa-write-key", WRITE_KEY);
    (document.head || document.getElementsByTagName("head")[0]).appendChild(
      script,
    );

    ra.load(WRITE_KEY, DATA_PLANE_URL, {});
  }, LOAD_FALLBACK_DELAY_MS);
};

/** Fire a track call (queues into the stub if the SDK isn't loaded yet). */
export const cdpTrack = (
  event: string,
  properties?: Record<string, unknown>,
): void => {
  if (!CDP_ENABLED) return;
  ensureStub();
  getRA()?.track(event, properties);
};

/** Associate the current visitor with a user id + traits. */
export const cdpIdentify = (
  userId: string,
  traits?: Record<string, unknown>,
): void => {
  if (!CDP_ENABLED) return;
  ensureStub();
  getRA()?.identify(userId, traits);
};

/** Page view. NOTE: the GTM snippet already fires page() — use sparingly. */
export const cdpPage = (
  category?: string,
  name?: string,
  properties?: Record<string, unknown>,
): void => {
  if (!CDP_ENABLED) return;
  ensureStub();
  getRA()?.page(category, name, properties);
};

/** Clear identity (e.g. on a fresh booking session). */
export const cdpReset = (): void => {
  if (!CDP_ENABLED) return;
  getRA()?.reset?.();
};
